import { Controller } from "@hotwired/stimulus"
import { moneyText } from "../helpers/money_text"
import {
  SPRING, EASE_IN_OUT, easeInOut, easeOutQuad, reducedMotion, wait, anim, tween,
  coinFly, trailBit, landDust, burst, glints, goldBits, confetti, floatLabel
} from "../helpers/goal_climb_fx"

// The climb on goals/progress. The server draws the finished scene: camps
// behind you gold, the next one's column filled with your money to how far
// through the leg you are, the rest unbuilt stone. This plays it in.
//
// Entrance: the stones rise, the walker climbs camp by camp (each column
// fills, turns to gold, then it hops on), and the next column fills to today.
//
// Money since the last visit: the page remembers the saved amount it last
// showed for this goal (localStorage, `key`). When it comes back different
// (a set-aside sheet just closed onto this screen, a skipped bill fed the
// goal), the entrance stops at the old amount and the difference plays:
// coins fly from Set aside into the column, the level rises, full columns turn
// to gold and the walker hops on. Money that left drains back into Take out
// and the walker slides down.
//
// Values:
//   camps   – [[x, y], …] pillar tops in scene units, start to summit
//   ranges  – visible column height per camp (GoalClimbScene#range)
//   amounts – camp amounts, start to summit
//   labels  – camp labels as engraved on the pillars
//   current – index of the camp the user stands on (server's view)
//   saved / target – the goal now
//   scene   – [width, height, top half width, top half height, floor]
//   key     – localStorage key for the last amount shown
//   money   – ApplicationHelper#money_text_format, for helpers/money_text
//   i18n    – copy templates (%{amount}, %{camp}, %{target}, %{pct})
const FEET = 9
const ARC_LIFT = 62
const START_DELAY_MS = 1100
const LEG_SPEEDS = [1, 0.72, 0.58, 0.5]

export default class extends Controller {
  static targets = ["walker", "art", "tag", "shadow", "block", "world", "cam", "chest", "bubble",
    "saved", "savedOf", "savedBar", "left", "hint", "meter", "setAside", "takeOut"]

  static values = {
    camps: Array, ranges: Array, amounts: Array, labels: Array, current: Number,
    saved: Number, target: Number, scene: Array, key: String, money: Object, i18n: Object
  }

  connect() {
    const previous = this.#remember()
    this.busy = true
    this.shown = this.savedValue

    if (reducedMotion()) {
      this.element.classList.add("is-in")
      this.#settleWalker(this.currentValue)
      this.busy = false
      return
    }

    const from = previous ?? this.savedValue
    this.#resetTo(this.#clamp(from))
    this.frame = requestAnimationFrame(() => this.element.classList.add("is-in"))
    this.#play(from)
  }

  disconnect() {
    this.gone = true
    if (this.frame) cancelAnimationFrame(this.frame)
    clearTimeout(this.bubbleTimer)
  }

  // MARK: - Taps

  // A tapped pillar sinks into its column while held.
  press(event) {
    event.target.closest?.(".gp-block")?.classList.add("is-pressed")
  }

  release() {
    this.blockTargets.forEach((block) => block.classList.remove("is-pressed"))
  }

  // Released: the column springs past rest and settles (a physical button),
  // whatever stands on it rides the bounce, and a bubble says what the camp is.
  tapBlock(event) {
    const block = event.target.closest?.(".gp-block")
    if (!block) return
    const i = Number(block.dataset.index)
    const [x, y] = this.campsValue[i]
    const floor = this.sceneValue[4]
    block.classList.remove("is-pressed")
    anim(block, [{ transform: "scale(1.025,.982)" }, { transform: "scale(.99,1.014)", offset: 0.38 }, { transform: "scale(1.006,.996)", offset: 0.7 }, { transform: "none" }],
      { duration: 540, easing: "ease-out" })
    const sink = (floor - y) * 0.018
    const ride = [{ transform: `translateY(${sink}px)` }, { transform: `translateY(${-sink * 0.8}px)`, offset: 0.38 }, { transform: `translateY(${sink * 0.25}px)`, offset: 0.7 }, { transform: "none" }]
    const cur = this.#campFor(this.shown)
    if (i === this.#summit) anim(this.chestTarget, ride, { duration: 540, easing: "ease-out" })
    if (i === cur && !this.busy) anim(this.walkerTarget, ride, { duration: 540, easing: "ease-out" })

    const t = this.i18nValue
    let note
    let tone = ""
    if (i <= cur) {
      tone = "ok"
      note = i === 0 ? t.started_here : t.reached
    } else {
      tone = i === cur + 1 ? "next" : ""
      note = t.to_go.replace("%{amount}", this.#money(this.amountsValue[i] - this.shown))
    }
    const lift = i === cur ? 60 : i === this.#summit ? 70 : 6
    this.#bubble(this.labelsValue[i], note, tone, [x, y - lift])
  }

  // The chest hops, the lid pops and a few coins jump out; it shuts again
  // unless the goal is reached.
  tapChest(event) {
    event.stopPropagation()
    this.#chestHop()
    const t = this.i18nValue
    const [x, y] = this.campsValue[this.#summit]
    const reached = this.shown >= this.targetValue
    const note = reached ? t.chest_open : t.chest_to_go.replace("%{amount}", this.#money(this.targetValue - this.shown))
    // On the dashboard (gp-embedded) the scene's top holds the hint and Set
    // aside, so the chest's bubble opens to its side instead of above it.
    const side = this.element.classList.contains("gp-embedded")
    this.#bubble(this.#money(this.targetValue), note, reached ? "ok" : "next", side ? [x, y - 24] : [x, y - 56], side)
  }

  // The walker answers a tap with a little jump in place.
  async tapWalker() {
    if (this.busy) return
    const art = this.artTarget
    this.walkerTarget.classList.remove("is-home")
    await anim(art, [{ transform: "none" }, { transform: "scale(1.15,.82)" }], { duration: 140, easing: "ease-out", fill: "forwards" })
    await anim(art, [{ transform: "scale(.9,1.12)" }, { transform: "translateY(-26px) scale(.95,1.06)", offset: 0.45 }, { transform: "translateY(0) scale(1.12,.86)", offset: 0.85 }, { transform: "none" }],
      { duration: 520, easing: "ease-out" })
    art.getAnimations().forEach((a) => a.cancel())
    this.walkerTarget.classList.add("is-home")
  }

  // MARK: - The show

  // The scene tops out at the target: money past it has nowhere to stand.
  async #play(previous) {
    const from = this.#clamp(previous)
    const to = this.#clamp(this.savedValue)
    await wait(START_DELAY_MS)
    await this.#climbTo(from)
    if (this.gone) return
    if (from < to) await this.#moneyIn(from, to)
    else if (from > to) await this.#moneyOut(from, to)
    if (this.gone) return
    this.#paintCopy(this.savedValue)
    this.busy = false
  }

  // The walker climbs from the start to the camp `value` stands on; the next
  // column then fills to it.
  async #climbTo(value) {
    const cur = this.#campFor(value)
    for (let i = 0; i < cur && !this.gone; i++) {
      await this.#levelTo(i + 1, 1, { ms: 240 })
      await this.#solidify(i + 1, { speed: 0.45, quiet: true })
      this.#makeNext(i + 2, 0)
      await this.#hop(i, { fast: true })
      await wait(90)
    }
    this.#walkerHome()
    if (cur < this.#summit) {
      await this.#levelTo(cur + 1, this.#legP(cur + 1, value), { ms: 900, slosh: 5 })
    } else {
      this.#block(this.#summit)?.classList.add("is-lit")
      this.chestTarget.classList.add("is-open")
    }
  }

  // Money came in: it builds the next camp. Fill a column to the top and it
  // turns to gold, then the walker hops on. Chains across camps, faster each
  // time.
  async #moneyIn(from, to) {
    const source = this.hasSetAsideTarget ? this.setAsideTarget : null
    let saved = from
    let i = this.#campFor(saved) + 1
    let leg = 0
    let plused = false
    this.walkerTarget.classList.remove("is-home")
    this.#camTo(this.#mid(this.campsValue[i - 1], this.campsValue[i]), 1.1, 650)
    if (source) {
      await anim(source, [{ transform: "none" }, { transform: "translateY(3px) scale(.95)", offset: 0.45 }, { transform: "translateY(-1px) scale(1.02)", offset: 0.8 }, { transform: "none" }],
        { duration: 340, easing: "ease-out" })
    }
    while (saved < to && i <= this.#summit && !this.gone) {
      const speed = LEG_SPEEDS[Math.min(leg, LEG_SPEEDS.length - 1)]
      const legEnd = this.amountsValue[i]
      const chunkTo = Math.min(to, legEnd)
      const start = saved
      const n = this.#coinCount(chunkTo - start)
      let landed = 0
      await Promise.all([...Array(n)].map((_, k) => {
        const after = this.#legP(i, start + (chunkTo - start) * (k + 1) / n)
        return coinFly(k, this.#sourcePoint(source, k, n), this.#screenPoint(this.#surfacePoint(i, after)),
          { ms: 860 * speed, delay: k * 95 * speed, windup: leg === 0 }).then(() => {
          landed += 1
          const v = start + (chunkTo - start) * landed / n
          this.#paintCopy(v)
          this.#bumpSaved()
          this.#levelTo(i, this.#legP(i, v), { ms: 380 * speed })
        })
      }))
      saved = chunkTo
      this.#paintCopy(saved)
      if (!plused) {
        plused = true
        const [x, y] = this.campsValue[i]
        floatLabel(this.camTarget, "gp-plus", this.#pct([x, y - 80]), `+${this.#money(to - from)}`)
      }
      if (saved < legEnd) {
        await this.#levelTo(i, this.#legP(i, saved), { ms: 780, slosh: 7 })
        break
      }
      // Full: slosh at the brim, set to gold, then the walker hops on.
      await this.#levelTo(i, 1, { ms: 560 * speed, slosh: 5 })
      await this.#solidify(i, { speed })
      const [x, y] = this.campsValue[i]
      if (i < this.#summit) {
        floatLabel(this.camTarget, "gp-pop", this.#pct([x, y - 74]), this.i18nValue.camp_pop.replace("%{amount}", this.labelsValue[i]), 900)
      }
      this.#camTo(this.#mid(this.campsValue[i - 1], this.campsValue[i]), 1.1, 420 * speed)
      await this.#hop(i - 1, { speed })
      if (i === this.#summit) {
        confetti()
        this.#chestHop({ big: true })
        floatLabel(this.camTarget, "gp-pop", this.#pct([x, y - 110]), this.i18nValue.goal_pop, 900)
        break
      }
      i += 1
      leg += 1
      this.#makeNext(i, 0)
      if (saved < to) {
        this.#camTo(this.#mid(this.campsValue[i - 1], this.campsValue[i]), 1.1, 420)
        await wait(260 * speed)
      }
    }
    this.#walkerHome()
    await wait(300)
    this.#camHome()
  }

  // Money left: coins leave the column's surface for Take out, the level
  // drains, and below a camp the walker slides back down while that camp
  // loses its gold (still full, ready to drain). Restrained, no celebration.
  async #moneyOut(from, to) {
    const sink = this.hasTakeOutTarget ? this.takeOutTarget : (this.hasSetAsideTarget ? this.setAsideTarget : null)
    let saved = from
    let cur = this.#campFor(saved)
    this.walkerTarget.classList.remove("is-home")
    while (saved > to && cur >= 0 && !this.gone) {
      const floorV = this.amountsValue[cur]
      if (saved > floorV && cur < this.#summit) {
        const i = cur + 1
        const start = saved
        const chunkTo = Math.max(to, floorV)
        const n = this.#coinCount(start - chunkTo)
        this.#camTo(this.#mid(this.campsValue[cur], this.campsValue[i]), 1.06, 500)
        let gone = 0
        await Promise.all([...Array(n)].map((_, k) =>
          coinFly(k, this.#screenPoint(this.#surfacePoint(i, this.#legP(i, start))), this.#sourcePoint(sink, k, n),
            { ms: 620, delay: k * 90, windup: false, spin: -420, lift: 40 }).then(() => {
            gone += 1
            const v = start - (start - chunkTo) * gone / n
            this.#paintCopy(v)
            this.#bumpSaved()
            this.#levelTo(i, this.#legP(i, v), { ms: 360 })
          })))
        saved = chunkTo
        this.#paintCopy(saved)
        await wait(200)
        if (saved <= to) break
        continue
      }
      // Below this camp now: the column ahead goes back to stone, the walker
      // steps down, and this camp loses its gold.
      if (cur === this.#summit) this.chestTarget.classList.remove("is-open")
      else this.#setState(cur + 1, "unbuilt")
      this.#camTo(this.#mid(this.campsValue[cur - 1], this.campsValue[cur]), 1.06, 500)
      await this.#hopBack(cur - 1)
      this.#makeNext(cur, 1)
      const [x, y] = this.campsValue[cur]
      const back = cur - 1 ? this.i18nValue.back_to.replace("%{amount}", this.labelsValue[cur - 1]) : this.i18nValue.back_to_start
      floatLabel(this.camTarget, "gp-pop is-back", this.#pct([x, y - 60]), back, 900)
      cur -= 1
      if (saved <= to) break
      await wait(320)
    }
    this.#walkerHome()
    this.#camHome()
  }

  // MARK: - Walker

  // One hop along leg i: crouch (anticipation), stretch into the air, follow
  // the arc, squash on landing, the pillar takes the weight (follow-through),
  // the tag swings a beat behind (overlap). `fast` is the entrance's quick
  // climb.
  async #hop(i, { fast = false, speed = 1 } = {}) {
    const [ax] = this.campsValue[i]
    const [bx] = this.campsValue[i + 1]
    const dir = bx < ax ? -1 : 1
    const walker = this.walkerTarget
    const art = this.artTarget
    walker.classList.toggle("is-left", dir < 0)
    walker.classList.remove("is-squash", "is-home")
    if (!fast) await anim(art, [{ transform: "none" }, { transform: "scale(1.2,.76)" }], { duration: 220 * speed, easing: "ease-out", fill: "forwards" })
    const ms = fast ? 430 : 600 * speed
    anim(art, [{ transform: fast ? "none" : "scale(1.2,.76)" }, { transform: "scale(.84,1.2)", offset: 0.22 }, { transform: "scale(1)", offset: 0.7 }, { transform: "scale(1)" }], { duration: ms, easing: "ease-out" })
    anim(this.tagTarget, [{ rotate: "0deg" }, { rotate: `${-dir * 14}deg`, offset: 0.3 }, { rotate: `${dir * 8}deg`, offset: 0.75 }, { rotate: "0deg" }], { duration: ms + 300, easing: "ease-out" })
    this.#stamp(i)
    let lastBit = 0
    await tween(ms, (e) => {
      const [x, y] = this.#arcPoint(i, e)
      if (e - lastBit > 0.08 && e > 0.1 && e < 0.92) {
        lastBit = e
        trailBit(this.camTarget, this.#pct([x, y + FEET + 4]), "spark")
      }
      this.#place([x, y + FEET])
      this.#lift(Math.sin(Math.PI * e) * 22)
    }, fast ? easeOutQuad : easeInOut)
    this.#lift(0)
    art.getAnimations().forEach((a) => a.cancel())
    void walker.offsetWidth
    walker.classList.add("is-squash")
    anim(this.#block(i + 1), [{ transform: "translateY(0)" }, { transform: "translateY(6px)", offset: 0.3 }, { transform: "translateY(-2px)", offset: 0.7 }, { transform: "translateY(0)" }],
      { duration: 420, easing: "ease-out" })
    if (fast) return
    const [lx, ly] = this.campsValue[i + 1]
    landDust(this.camTarget, this.#pct([lx, ly + 2]))
    anim(this.tagTarget, [{ translate: "0 0" }, { translate: "0 8px", offset: 0.3 }, { translate: "0 -3px", offset: 0.65 }, { translate: "0 0" }],
      { duration: 560, delay: 70, easing: "ease-out" })
  }

  // One leg backwards: tugged (leans back), then slides down the arc to the
  // camp below, dust under its feet.
  async #hopBack(i) {
    const dir = this.campsValue[i][0] < this.campsValue[i + 1][0] ? -1 : 1
    const walker = this.walkerTarget
    const art = this.artTarget
    walker.classList.toggle("is-left", dir < 0)
    walker.classList.remove("is-squash", "is-home")
    await anim(art, [{ transform: "none" }, { transform: `rotate(${dir * 12}deg) scale(.94,1.06)` }], { duration: 200, easing: "ease-out", fill: "forwards" })
    anim(art, [{ transform: `rotate(${dir * 12}deg) scale(.94,1.06)` }, { transform: `rotate(${-dir * 6}deg) scale(1.04,.96)`, offset: 0.6 }, { transform: "none" }], { duration: 640, easing: "ease-out" })
    anim(this.tagTarget, [{ rotate: "0deg" }, { rotate: `${dir * 14}deg`, offset: 0.4 }, { rotate: `${-dir * 6}deg`, offset: 0.8 }, { rotate: "0deg" }], { duration: 900, easing: "ease-out" })
    this.#unstamp(i)
    let lastBit = 0
    await tween(640, (e) => {
      const [x, y] = this.#arcPoint(i, 1 - e)
      this.#place([x, y + FEET])
      if (e - lastBit > 0.1) {
        lastBit = e
        trailBit(this.camTarget, this.#pct([x, y + FEET + 2]), "dust")
      }
    })
    art.getAnimations().forEach((a) => a.cancel())
    void walker.offsetWidth
    walker.classList.add("is-squash")
    anim(this.#block(i), [{ transform: "translateY(0)" }, { transform: "translateY(5px)", offset: 0.3 }, { transform: "translateY(0)" }], { duration: 360, easing: "ease-out" })
  }

  #walkerHome() {
    this.walkerTarget.classList.remove("is-left", "is-squash")
    this.walkerTarget.classList.add("is-home")
  }

  #settleWalker(index) {
    const [x, y] = this.campsValue[index]
    this.#place([x, y])
    this.#walkerHome()
  }

  #place([x, y]) {
    const [width, height] = this.sceneValue
    this.walkerTarget.style.left = `${(x / width) * 100}%`
    this.walkerTarget.style.top = `${(y / height) * 100}%`
  }

  // The contact shadow stays on the stone and shrinks as the walker rises.
  #lift(px) {
    this.shadowTarget.style.transform = `translate(-50%, ${px}px) scale(${1 - px / 46})`
    this.shadowTarget.style.opacity = 1 - px / 40
  }

  // The hop path: a quadratic arc from one top face to the next.
  #arcPoint(i, t) {
    const [ax, ay] = this.campsValue[i]
    const [bx, by] = this.campsValue[i + 1]
    const p0 = [ax, ay - FEET]
    const p1 = [(ax + bx) / 2, Math.min(ay, by) - FEET - ARC_LIFT]
    const p2 = [bx, by - FEET]
    const u = 1 - t
    return [0, 1].map((k) => u * u * p0[k] + 2 * u * t * p1[k] + t * t * p2[k])
  }

  // Two paw prints pressed into the camp being left, pointing at the next.
  #stamp(i) {
    const prints = this.#block(i)?.querySelector(".gp-prints")
    if (!prints || !prints.dataset.angle) return
    prints.innerHTML = `<g transform="rotate(${prints.dataset.angle})"><ellipse class="gp-paw" cx="-8" cy="-6" rx="7" ry="4.4"/><ellipse class="gp-paw" cx="7" cy="6" rx="7" ry="4.4"/></g>`
    prints.querySelectorAll(".gp-paw").forEach((paw, k) => {
      anim(paw, [{ opacity: 0 }, { opacity: 1 }], { duration: 320, delay: k * 120, easing: "ease-out", fill: "backwards" })
    })
  }

  #unstamp(i) {
    const prints = this.#block(i)?.querySelector(".gp-prints")
    if (!prints) return
    anim(prints, [{ opacity: 1 }, { opacity: 0 }], { duration: 400 }).then(() => { prints.innerHTML = "" })
  }

  // MARK: - Columns

  // Back to the scene as it stood at `value`, before the entrance starts:
  // only the start banked, the first column empty, the walker at the start.
  #resetTo(value) {
    this.blockTargets.forEach((block) => {
      const i = Number(block.dataset.index)
      this.#setState(i, i === 0 ? "lit" : i === 1 ? "next" : "unbuilt")
      this.#levelTo(i, 0, { ms: 0 })
      if (i > 0) block.querySelector(".gp-prints").innerHTML = ""
    })
    this.chestTarget.classList.remove("is-open")
    this.walkerTarget.classList.remove("is-home", "is-left", "is-squash")
    this.#place(this.campsValue[0])
    this.#paintCopy(value)
  }

  #setState(i, state) {
    const block = this.#block(i)
    if (!block) return
    block.classList.remove("is-lit", "is-next", "is-unbuilt")
    block.classList.add(`is-${state}`)
  }

  #makeNext(i, level) {
    if (i > this.#summit) return
    this.#setState(i, "next")
    this.#levelTo(i, level, { ms: 0 })
  }

  // Move the money level. Slow in and out; with slosh it overshoots, dips
  // back and settles while the surface tips (follow-through). Transform only.
  #levelTo(i, level, { ms = 520, slosh = 0 } = {}) {
    const block = this.#block(i)
    const liquid = block?.querySelector(".gp-lq")
    if (!liquid) return Promise.resolve()
    const from = this.#offsetOf(liquid)
    const to = this.#fillOffset(i, level)
    liquid.getAnimations().forEach((a) => a.cancel())
    liquid.style.transform = `translateY(${to}px)`
    if (reducedMotion() || !ms) return Promise.resolve()
    const d = from >= to ? 1 : -1
    if (slosh) {
      anim(block.querySelector(".gp-tilt"), [{ transform: "rotate(0)" }, { transform: `rotate(${3.5 * d}deg)`, offset: 0.3 }, { transform: `rotate(${-2 * d}deg)`, offset: 0.62 }, { transform: `rotate(${0.8 * d}deg)`, offset: 0.84 }, { transform: "rotate(0)" }],
        { duration: ms, easing: "ease-in-out" })
    }
    const frames = slosh
      ? [{ transform: `translateY(${from}px)`, easing: EASE_IN_OUT }, { transform: `translateY(${to - d * slosh}px)`, offset: 0.6, easing: "ease-in-out" }, { transform: `translateY(${to + d * slosh * 0.35}px)`, offset: 0.82, easing: "ease-in-out" }, { transform: `translateY(${to}px)` }]
      : [{ transform: `translateY(${from}px)` }, { transform: `translateY(${to}px)` }]
    return anim(liquid, frames, { duration: ms, easing: slosh ? "linear" : EASE_IN_OUT })
  }

  // A full column turns to gold: the money takes the gold first, the column
  // squashes, stretches and settles, sparkles fly off the top.
  async #solidify(i, { speed = 1, quiet = false } = {}) {
    const block = this.#block(i)
    if (!block) return
    const liquid = block.querySelector(".gp-liquid")
    block.classList.add("is-solid")
    this.#setState(i, "lit")
    if (liquid) anim(liquid, [{ opacity: 1 }, { opacity: 1, offset: 0.55 }, { opacity: 0 }], { duration: 560 * speed, easing: "ease-in", fill: "forwards" })
    anim(block, [{ transform: "none" }, { transform: "scale(1.08,.95)", offset: 0.22 }, { transform: "scale(.95,1.05)", offset: 0.5 }, { transform: "scale(1.02,.99)", offset: 0.76 }, { transform: "none" }],
      { duration: 640 * speed, easing: "ease-out" })
    if (!quiet) {
      const [x, y] = this.campsValue[i]
      burst(this.camTarget, this.#pct([x, y - 4]), 12)
      glints(this.camTarget, this.#pct([x, y]), [[-30, -8], [28, -14], [4, -30], [-14, 14]].map((delta) => this.#pct(delta)))
    }
    await wait(560 * speed)
    liquid?.getAnimations().forEach((a) => a.cancel())
    block.classList.remove("is-solid")
  }

  #fillOffset(i, level) {
    const th = this.sceneValue[3]
    const range = this.rangesValue[i]
    return level <= 0.001 ? range + 2 * th : (1 - level) * (range + th)
  }

  #offsetOf(el) {
    try {
      return new DOMMatrix(getComputedStyle(el).transform).f
    } catch {
      return 0
    }
  }

  // Where the money's surface sits in column i at a level, in scene units.
  #surfacePoint(i, level) {
    const [cx, cy] = this.campsValue[i]
    const th = this.sceneValue[3]
    return [cx, Math.min(cy + this.rangesValue[i] + th * 0.5, Math.max(cy + 2, cy + this.#fillOffset(i, level)))]
  }

  // MARK: - Chest, camera, copy

  async #chestHop({ big = false } = {}) {
    const chest = this.chestTarget
    if (chest.dataset.busy) return
    chest.dataset.busy = "1"
    await anim(chest, [{ transform: "none" }, { transform: "scale(1.16,.82)" }], { duration: 140, easing: "ease-out", fill: "forwards" })
    const up = anim(chest, [{ transform: "scale(1.16,.82)" }, { transform: `translateY(${big ? -30 : -20}px) scale(.9,1.12)`, offset: 0.4 }, { transform: "translateY(0) scale(1.14,.86)", offset: 0.8 }, { transform: "none" }],
      { duration: 560, easing: "ease-out" })
    await wait(200)
    chest.classList.add("is-open")
    const [x, y] = this.campsValue[this.#summit]
    goldBits(this.camTarget, this.#pct([x, y - 34]), big ? 16 : 9)
    await up
    chest.getAnimations().forEach((a) => a.cancel())
    if (this.shown < this.targetValue) {
      await wait(big ? 1600 : 900)
      chest.classList.remove("is-open")
      await wait(300)
      anim(chest, [{ transform: "scale(1)" }, { transform: "scale(1.08,.9)", offset: 0.4 }, { transform: "none" }], { duration: 240, easing: "ease-out" })
    }
    delete chest.dataset.busy
  }

  // Staging: the scene leans in on a point while money moves, then eases back.
  #camTo([x, y], zoom, ms) {
    const cam = this.camTarget
    const [width, height] = this.sceneValue
    const current = getComputedStyle(cam).transform
    cam.style.transformOrigin = `${(x / width) * 100}% ${((y - 30) / height) * 100}%`
    return anim(cam, [{ transform: current === "none" ? "scale(1)" : current }, { transform: `scale(${zoom})` }], { duration: ms, easing: EASE_IN_OUT, fill: "forwards" })
  }

  #camHome() {
    const cam = this.camTarget
    anim(cam, [{ transform: getComputedStyle(cam).transform }, { transform: "scale(1)" }], { duration: 800, easing: EASE_IN_OUT, fill: "forwards" })
      .then(() => cam.getAnimations().forEach((a) => a.cancel()))
  }

  #bubble(title, note, tone, point, side = false) {
    const bubble = this.bubbleTarget
    bubble.classList.toggle("is-side", side)
    bubble.querySelector("b").textContent = title
    const span = bubble.querySelector("span")
    span.className = tone
    span.textContent = note
    const [left, top] = this.#pct(point)
    bubble.style.left = `${left}%`
    bubble.style.top = `${top}%`
    bubble.classList.remove("is-on")
    void bubble.offsetWidth
    bubble.classList.add("is-on")
    clearTimeout(this.bubbleTimer)
    this.bubbleTimer = setTimeout(() => bubble.classList.remove("is-on"), 2200)
  }

  // The header, the Saved card and the next-camp line, at `value`.
  #paintCopy(value) {
    this.shown = value
    const t = this.i18nValue
    const target = this.targetValue
    const pct = target > 0 ? Math.min(100, Math.max(0, Math.floor((value / target) * 100))) : 0
    if (this.hasLeftTarget) this.leftTarget.textContent = this.#money(Math.ceil(Math.max(target - value, 0)))
    if (this.hasSavedTarget) this.savedTarget.textContent = this.#money(value)
    if (this.hasSavedOfTarget) this.savedOfTarget.textContent = t.saved_of.replace("%{target}", this.#money(target)).replace("%{pct}", pct)
    if (this.hasSavedBarTarget) this.savedBarTarget.style.width = `${Math.max(pct, 2)}%`
    if (!this.hasHintTarget) return
    const cur = this.#campFor(value)
    if (cur >= this.#summit) {
      this.hintTarget.textContent = t.hint_done
      return
    }
    const next = cur + 1
    const amount = `<b>${this.#money(this.amountsValue[next] - value)}</b>`
    this.hintTarget.innerHTML = next === this.#summit
      ? t.hint_top.replace("%{amount}", amount)
      : t.hint_camp.replace("%{amount}", amount).replace("%{camp}", this.labelsValue[next])
    if (this.hasMeterTarget) this.meterTarget.style.width = `${this.#legP(next, value) * 100}%`
  }

  #bumpSaved() {
    if (!this.hasSavedTarget) return
    anim(this.savedTarget, [{ transform: "scale(1)" }, { transform: "scale(1.18,.9)", offset: 0.3 }, { transform: "scale(.97,1.04)", offset: 0.65 }, { transform: "scale(1)" }],
      { duration: 300, easing: SPRING })
  }

  // MARK: - Memory and maths

  // The amount this page last showed for the goal, when it differs from now
  // and the target is the same. The new amount is written straight away, so a
  // reload never plays the same money twice.
  #remember() {
    let previous = null
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.keyValue) || "null")
      if (stored && stored.target === this.targetValue && Math.abs(stored.saved - this.savedValue) >= 0.5) previous = stored.saved
      window.localStorage.setItem(this.keyValue, JSON.stringify({ saved: this.savedValue, target: this.targetValue }))
    } catch {
      previous = null
    }
    return previous
  }

  get #summit() {
    return this.campsValue.length - 1
  }

  #campFor(value) {
    if (value >= this.targetValue) return this.#summit
    let index = 0
    for (let i = 1; i < this.#summit; i++) if (value >= this.amountsValue[i]) index = i
    return index
  }

  // How full column i is at `value`: through the leg from camp i-1 to camp i.
  #legP(i, value) {
    const from = this.amountsValue[i - 1]
    const to = this.amountsValue[i]
    if (to <= from) return 0
    return Math.min(1, Math.max(0, (value - from) / (to - from)))
  }

  #coinCount(amount) {
    if (reducedMotion()) return 1
    const share = this.targetValue > 0 ? amount / this.targetValue : 0
    return Math.max(2, Math.min(6, Math.round(share * 40)))
  }

  #money(amount) {
    return moneyText(Math.round(amount), this.moneyValue)
  }

  #block(index) {
    return this.blockTargets.find((block) => Number(block.dataset.index) === index)
  }

  #mid([ax, ay], [bx, by]) {
    return [(ax + bx) / 2, (ay + by) / 2]
  }

  #pct([x, y]) {
    const [width, height] = this.sceneValue
    return [(x / width) * 100, (y / height) * 100]
  }

  #clamp(value) {
    return Math.min(Math.max(value, 0), this.targetValue)
  }

  // A scene point on screen, following the camera zoom.
  #screenPoint([x, y]) {
    const rect = this.worldTarget.getBoundingClientRect()
    const [width, height] = this.sceneValue
    return [rect.left + (x / width) * rect.width, rect.top + (y / height) * rect.height]
  }

  // Where coins leave from (or land in): across the top of a button, or the
  // bottom middle of the scene when there is no button.
  #sourcePoint(el, k, n) {
    const rect = el ? el.getBoundingClientRect() : this.worldTarget.getBoundingClientRect()
    const y = el ? rect.top + 6 : rect.bottom - 10
    return [rect.left + rect.width / 2 + (k - (n - 1) / 2) * 16, y]
  }
}
